// L2-followup of docs/planning/firefly_role_context_presentation.md:
// the "Peek readiness" diagnostic that the historical
// `DevPanelActivityDockPeekStatsSection` was missing. The historical
// view tells you what HAS peeked; this view tells you whether a peek
// COULD fire right now, and if not, what's blocking it.
//
// The three blockers we surface (in the order a developer should
// check them):
//
//   1. Assist level. If it's `off` or `minimal`, `canPeekOnModeChange`
//      returns false and the peek hook short-circuits before any
//      signal even gets evaluated. Cheapest and most common cause of
//      "I changed buckets and nothing peeked."
//   2. No signal. For each role-visible, peek-eligible section
//      (`section.hasSignal != null`), we evaluate `hasSignal` against
//      a freshly-built `DockSectionPeekContext` and show the result.
//      "all sections say no signal" is the second most common cause.
//   3. Wrong bucket / mode. Per-mode projection: for every production
//      mode, we run the full composition (`useActivityDockSections`
//      role-filtered list → `filterDockSectionsByBucket` for that
//      mode's bucket → `evaluateDockPeek` with that mode) and show
//      which mode would earn a peek with the CURRENT signal context.
//      Tells the user "switch to mode X and you'll get a peek" without
//      them having to brute-force every nav.
//
// Doctrine:
//   - Passive observer. No "force fire" button — same reason the
//     parent stats panel doesn't have one (would pollute historical
//     stats with non-real peeks).
//   - Session-dedup is intentionally NOT instrumented here. It's a
//     lifecycle gate inside `useActivityDockModePeek` that lives on
//     `useRef`, not on any subscribable store; instrumenting it would
//     mean a refactor of the dedup mechanism. The "reload tab to
//     reset" hint covers the diagnostic case for now.
//   - File extracted from `DevPanelActivityDockPeekStatsSection.tsx`
//     to keep the parent under the 300-line soft cap.
//
// Projection-only signal override (added 2026-06-05, revised same day):
//   - A platform_dev viewing the probe without impersonating
//     sales/manufacturing has ZERO peek-eligible sections in scope
//     (the role-gate filters them all out before the probe sees
//     them). Without bypassing roles too, flipping the override
//     made nothing visibly change — the section table stayed at
//     its empty-state and every mode-projection row stayed silent.
//   - Revised design: when override.enabled, the probe ALSO
//     synthesizes role membership for the peek-driving roles
//     (`PEEK_DRIVING_ROLES` below). The probe then recomputes the
//     section list via the pure `getDockSectionsForAuth` with the
//     synthetic role predicate, so sections that require sales /
//     manufacturing / ads_guidance_editor light up in the table and
//     drive per-mode projections.
//   - Doctrine that did NOT change: the LIVE
//     `useActivityDockPeekDecision` is untouched. Override never
//     fires a real peek, never emits an `ACTIVITY_DOCK_PEEK_EVENT`,
//     never pollutes the historical-events panel below.
//   - Override state persists across mount cycles via
//     `peekProbeOverrideStorage` (localStorage) so a dev's
//     configuration survives mode navigations that unmount the
//     parent DevPanel section.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import { useActivityDockSections } from "@/components/activity/useActivityDockSections";
import { useActivityDockAssistLevel } from "@/components/activity/useActivityDockAssistLevel";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import {
  filterDockSectionsByBucket,
  getDockSectionsForAuth,
} from "@/components/activity/sectionGate";
import {
  buildDockPeekContext,
  evaluateDockPeek,
} from "@/components/activity/dockPeekDecision";
import type { DockSectionPeekContext } from "@/components/activity/sectionRegistry";
import {
  getBucketForMode,
  MODE_TO_BUCKET,
} from "@/components/shell/modes/bucketsRegistry";
import { canPeekOnModeChange } from "@/lib/activityDock/assistLevel";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  loadPeekProbeOverride,
  savePeekProbeOverride,
  type PeekProbeSignalOverride,
} from "@/lib/devTools/peekProbeOverrideStorage";
import { PeekProbeSignalOverrideControls } from "./PeekProbeSignalOverrideControls";
import { PeekProbeLifecycleRow } from "./PeekProbeLifecycleRow";

// Roles the override synthesizes membership for when enabled. Covers
// every role currently named by any `roleGate` on a peek-eligible
// section (i.e. any section that has a `hasSignal` predicate). If a
// future peek-eligible section gates on a new role, add it here so
// the override stays a full counterfactual.
const PEEK_DRIVING_ROLES: ReadonlySet<string> = new Set([
  "sales",
  "ads_guidance_editor",
  "manufacturing",
]);

export function DevPanelActivityDockPeekReadinessProbe() {
  const auth = useAuth();
  const urgency = useWorkspaceUrgencyContext();
  const { level: assistLevel } = useActivityDockAssistLevel();
  const realHasCapability = useUserCapabilities();
  const [override, setOverride] = useState<PeekProbeSignalOverride>(
    loadPeekProbeOverride,
  );

  // Persist override edits across mount cycles. The parent
  // (`DevPanelActivityDockPeekStatsSection`) unmounts whenever the
  // user navigates off a DevPanel-visible mode, which would
  // otherwise wipe the override config every time.
  useEffect(() => {
    savePeekProbeOverride(override);
  }, [override]);

  // Real role-visible section list (used when override is off).
  const liveRoleVisibleSections = useActivityDockSections(null);

  // When override is enabled, synthesize role membership for every
  // peek-driving role so the section list isn't filtered down to
  // the dev's actual roles. Real capabilities still apply; the
  // section gate is union (RP-3) so synthetic roles alone are
  // enough to admit every peek-eligible section.
  const overrideRoleVisibleSections = useMemo(
    () =>
      override.enabled
        ? getDockSectionsForAuth(
            (...roles: string[]) =>
              roles.some(
                (r) => PEEK_DRIVING_ROLES.has(r) || auth.hasRole(r),
              ),
            realHasCapability,
            null,
          )
        : liveRoleVisibleSections,
    [override.enabled, liveRoleVisibleSections, auth, realHasCapability],
  );

  const livePeekContext = useMemo(
    () =>
      buildDockPeekContext({
        leads: urgency?.leads ?? [],
        adsActivity: urgency?.adsActivity ?? null,
        hasRole: (...roles: string[]) => auth.hasRole(...roles),
        authStatus: auth.status,
      }),
    [auth, urgency],
  );

  // When the override is on: synthesize signal values AND synthesize
  // role membership inside the peekContext (so a section's own
  // `hasSignal` role-check, e.g. `ctx.hasRole("sales")` inside
  // ads-activity, also passes). Capabilities aren't part of the
  // peek context — that gate is already handled at the registry
  // filter step above.
  const effectivePeekContext: DockSectionPeekContext = useMemo(() => {
    if (!override.enabled) return livePeekContext;
    return {
      hasRole: (...roles: string[]) =>
        roles.some((r) => PEEK_DRIVING_ROLES.has(r) || auth.hasRole(r)),
      unsentCount: override.unsentCount,
      adsTone: override.adsTone,
    };
  }, [override, livePeekContext, auth]);

  const peekEligibleSections = useMemo(
    () => overrideRoleVisibleSections.filter((s) => s.hasSignal != null),
    [overrideRoleVisibleSections],
  );

  const sectionRows = useMemo(
    () =>
      peekEligibleSections.map((section) => ({
        id: section.id,
        modeRelevance: section.modeRelevance ?? null,
        bucketRelevance: section.bucketRelevance ?? null,
        hasSignalNow: section.hasSignal!(effectivePeekContext),
      })),
    [peekEligibleSections, effectivePeekContext],
  );

  const peeksAllowedByAssist = canPeekOnModeChange(assistLevel);

  const modeProjection = useMemo(() => {
    const modes = Object.keys(MODE_TO_BUCKET) as PrimaryMode[];
    return modes
      .map((mode) => {
        const bucket = getBucketForMode(mode);
        const bucketFiltered = filterDockSectionsByBucket(
          overrideRoleVisibleSections,
          bucket,
        );
        const decision = evaluateDockPeek({
          sections: bucketFiltered,
          currentMode: mode,
          peekContext: effectivePeekContext,
        });
        return { mode, bucket, ...decision };
      })
      .sort((a, b) => a.mode.localeCompare(b.mode));
  }, [overrideRoleVisibleSections, effectivePeekContext]);

  const wouldPeekCount = modeProjection.filter((r) => r.shouldPeek).length;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-700/60 dark:bg-amber-900/15">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
          Peek readiness probe
          {override.enabled ? (
            <span className="ml-2 rounded bg-purple-200 px-1.5 py-0.5 text-[10px] font-normal normal-case text-purple-900 dark:bg-purple-900/60 dark:text-purple-100">
              override active
            </span>
          ) : null}
        </h4>
        <span className="text-[11px] text-amber-800 dark:text-amber-300">
          {peeksAllowedByAssist
            ? `${wouldPeekCount} mode${wouldPeekCount === 1 ? "" : "s"} would peek right now`
            : "Peeks blocked by assist level"}
        </span>
      </div>

      <dl className="mb-2 grid grid-cols-1 gap-1 text-[11px] sm:grid-cols-2">
        <div className="flex items-baseline gap-1">
          <dt className="font-semibold text-gray-700 dark:text-zinc-300">
            Assist level:
          </dt>
          <dd className="font-mono text-gray-900 dark:text-zinc-100">
            {assistLevel}
          </dd>
          <span
            className={
              peeksAllowedByAssist
                ? "ml-1 rounded bg-emerald-100 px-1.5 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                : "ml-1 rounded bg-rose-100 px-1.5 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200"
            }
          >
            {peeksAllowedByAssist ? "peeks allowed" : "peeks blocked"}
          </span>
        </div>
        <div className="flex items-baseline gap-1">
          <dt className="font-semibold text-gray-700 dark:text-zinc-300">
            Signals:
          </dt>
          <dd className="text-gray-900 dark:text-zinc-100">
            unsentCount=
            <span className="font-mono">
              {effectivePeekContext.unsentCount}
            </span>
            {" · "}
            adsTone=
            <span className="font-mono">
              {String(effectivePeekContext.adsTone)}
            </span>
            {override.enabled ? (
              <span className="ml-1 text-[10px] text-purple-700 dark:text-purple-300">
                (live: unsent={livePeekContext.unsentCount}, ads=
                {String(livePeekContext.adsTone)})
              </span>
            ) : null}
          </dd>
        </div>
      </dl>

      <PeekProbeSignalOverrideControls
        override={override}
        setOverride={setOverride}
      />

      <div className="mb-2">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-zinc-300">
          Peek-eligible sections (role-visible, has signal predicate)
        </div>
        {sectionRows.length === 0 ? (
          <p className="text-[11px] text-gray-600 dark:text-zinc-400">
            No peek-eligible sections for your current roles. Impersonate
            sales or manufacturing to surface their peek-driving sections.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {sectionRows.map((row) => (
              <li key={row.id} className="text-[11px]">
                <span className="font-mono text-gray-900 dark:text-zinc-100">
                  {row.id}
                </span>
                <span
                  className={
                    row.hasSignalNow
                      ? "ml-2 rounded bg-emerald-100 px-1.5 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                      : "ml-2 rounded bg-gray-200 px-1.5 text-gray-700 dark:bg-zinc-700/60 dark:text-zinc-300"
                  }
                >
                  {row.hasSignalNow ? "signal" : "no signal"}
                </span>
                <span className="ml-2 text-gray-600 dark:text-zinc-400">
                  modes:{" "}
                  <span className="font-mono">
                    {row.modeRelevance ? row.modeRelevance.join(",") : "*"}
                  </span>
                  {row.bucketRelevance ? (
                    <>
                      {" · buckets: "}
                      <span className="font-mono">
                        {row.bucketRelevance.join(",")}
                      </span>
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-zinc-300">
          Per-mode projection — would a peek fire if you switched to…?
        </div>
        <ul className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
          {modeProjection.map((row) => (
            <li key={row.mode} className="text-[11px]">
              <span className="font-mono text-gray-900 dark:text-zinc-100">
                {row.mode}
              </span>
              <span className="ml-1 text-gray-500 dark:text-zinc-400">
                ({row.bucket ?? "—"})
              </span>
              {row.shouldPeek ? (
                <>
                  <span className="ml-2 rounded bg-emerald-100 px-1.5 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
                    would peek
                  </span>
                  <span className="ml-2 text-gray-600 dark:text-zinc-400">
                    via{" "}
                    <span className="font-mono">{row.reasonSectionId}</span>
                  </span>
                </>
              ) : (
                <span className="ml-2 rounded bg-gray-200 px-1.5 text-gray-700 dark:bg-zinc-700/60 dark:text-zinc-300">
                  silent
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <PeekProbeLifecycleRow />
    </div>
  );
}
